import React, { useMemo, useState } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { useRebateTierProximity } from 'src/api/rebates/useRebateTierProximity';
import { CircleNotchIcon, DownloadIcon } from '@phosphor-icons/react';
import { TierProximityGroupedView } from './components/TierProximityGroupedView';
import { TierProximityFilters } from './components/TierProximityFilters';
import { downloadCSV } from '@/utils/csvDownload';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@erp/shared-utils';
import { ThresholdType } from '@erp/shared-types';
import {
  DataSourceSelect,
  type DataSourceType,
} from '@/components/common/DataSourceSelect';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function RebateTierProximityPage() {
  // Default to current UTC year
  const currentYear = new Date().getUTCFullYear();

  // Year selection - default to current year
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // Generate year options (current year and 5 years back)
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Data source selection - default to predictions
  const [dataSource, setDataSource] = useState<DataSourceType>('predictions');

  // Filter state - includes backend filters (thresholdType, retroactive, includeMissingBaseline, divisions) and frontend filters (supplier, tag, sort)
  const [filters, setFilters] = useState({
    supplierFilter: [] as string[],
    tagFilter: [] as string[],
    thresholdTypeFilter: [
      ThresholdType.GROWTH,
      ThresholdType.VOLUME,
    ] as string[],
    retroactiveFilter: [true] as boolean[],
    includeMissingBaseline: false,
    sortBy: 'bestValue' as
      | 'closest'
      | 'highestDelta'
      | 'progress'
      | 'bestValue'
      | 'bestRatio',
    divisionFilter: [] as string[], // Empty = show all divisions
    visibleColumns: [
      'currentVolume',
      'volumeNeeded',
      'rebateDelta',
      'netBenefit',
      'efficiencyRatio',
      'progress',
    ] as string[], // Default: show only essential columns
  });

  const { data, isLoading, error } = useRebateTierProximity({
    year: selectedYear,
    thresholdTypeFilter: filters.thresholdTypeFilter,
    retroactiveFilter: filters.retroactiveFilter,
    includeMissingBaseline: filters.includeMissingBaseline,
    divisionFilter: filters.divisionFilter,
    dataSource,
  });

  // Prepare CSV data - flatten the structure for export
  const csvData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item) => {
      return {
        'Supplier Name': item.supplierName,
        'Supplier Tags': item.supplierTags?.map((t) => t.name).join('; ') || '',
        'Agreement Name': item.agreementName,
        'Rule Name': item.ruleName,
        'Qualifying Product Groups':
          item.qualifyingProductGroups.length === 0
            ? 'All Products'
            : item.qualifyingProductGroups.map((pg) => pg.name).join('; '),
        'Current Tier':
          item.currentTier === null ? 'None' : `Tier ${item.currentTier}`,
        'Current Tier Rebate %':
          item.currentTierRebatePercentage === null
            ? ''
            : item.currentTierRebatePercentage.toFixed(2),
        'Next Tier': item.nextTier === null ? '-' : `Tier ${item.nextTier}`,
        'Next Tier Rebate %':
          item.nextTierRebatePercentage === null
            ? ''
            : item.nextTierRebatePercentage.toFixed(2),
        'Current Volume': formatCurrency(item.currentVolume),
        'Next Tier Threshold': item.nextTierThreshold
          ? formatCurrency(item.nextTierThreshold)
          : 'N/A',
        'Volume Needed': item.error
          ? item.error
          : item.volumeNeeded === null
            ? '-'
            : formatCurrency(item.volumeNeeded),
        'Current Rebate': item.error
          ? '-'
          : item.currentRebateAmount === null
            ? '-'
            : formatCurrency(item.currentRebateAmount),
        'Next Tier Rebate': item.error
          ? '-'
          : item.nextTierRebateAmount === null
            ? '-'
            : formatCurrency(item.nextTierRebateAmount),
        'Rebate Delta': item.error
          ? '-'
          : item.rebateDelta === null
            ? '-'
            : item.rebateDelta > 0
              ? `+${formatCurrency(item.rebateDelta)}`
              : formatCurrency(item.rebateDelta),
        'Net Benefit': item.error
          ? '-'
          : item.rebateDelta === null || item.volumeNeeded === null
            ? '-'
            : item.rebateDelta - item.volumeNeeded > 0
              ? `+${formatCurrency(item.rebateDelta - item.volumeNeeded)}`
              : formatCurrency(item.rebateDelta - item.volumeNeeded),
        'Efficiency Ratio': item.error
          ? '-'
          : item.rebateDelta === null || item.volumeNeeded === null
            ? '-'
            : (item.rebateDelta / item.volumeNeeded).toFixed(2),
        'Progress (%)': item.error
          ? ''
          : item.volumeNeeded === null
            ? '100'
            : Math.round(item.percentageComplete).toString(),
        Error: item.error || '',
      };
    });
  }, [data]);

  const handleDownloadCSV = () => {
    downloadCSV({
      data: csvData,
      filename: 'rebate-tier-proximity',
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center text-destructive">
            Error loading tier proximity data. Please try again.
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Rebate Tier Proximity</h1>
            <p className="text-muted-foreground mt-2">
              See which suppliers are closest to reaching the next rebate tier.
              Grouped by agreement, sorted by{' '}
              {filters.sortBy === 'closest'
                ? 'closest volume needed'
                : filters.sortBy === 'highestDelta'
                  ? 'highest rebate delta'
                  : filters.sortBy === 'progress'
                    ? 'progress percentage'
                    : filters.sortBy === 'bestValue'
                      ? 'best net benefit'
                      : 'best efficiency ratio'}
              .
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">
                Year:
              </Label>
              <Select
                value={String(selectedYear)}
                onValueChange={(val) => setSelectedYear(Number(val))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((year) => (
                    <SelectItem key={year} value={String(year)}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">
                Data Source:
              </Label>
              <DataSourceSelect
                value={dataSource}
                onChange={setDataSource}
                className="w-[180px]"
              />
            </div>
            {data && data.length > 0 && (
              <Button
                onClick={handleDownloadCSV}
                variant="outline"
                className="flex items-center gap-2"
              >
                <DownloadIcon className="h-4 w-4" />
                Download CSV
              </Button>
            )}
          </div>
        </div>

        {data && data.length > 0 && (
          <div className="mb-4">
            <TierProximityFilters
              data={data}
              filters={filters}
              onFiltersChange={setFilters}
            />
          </div>
        )}

        {data && data.length > 0 ? (
          <TierProximityGroupedView data={data} filters={filters} />
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <p>
              No tier proximity data available. Make sure you have agreements
              with tier-based rules.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default RebateTierProximityPage;
